import { useState, useEffect } from "react";
import { getApiUrl } from "../utils/api";
const API = getApiUrl();
import { useNavigate } from "react-router-dom";
import { Heart, ArrowLeft, Users, Download, Mail, Phone, Utensils, Check, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";


export default function RSVPManager() {
  const navigate = useNavigate();
  const [site, setSite] = useState(null);
  const [rsvps, setRsvps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const siteRes = await fetch(`${API}/sites/my`, { credentials: 'include' });
        if (siteRes.ok) {
          const siteData = await siteRes.json();
          if (siteData) {
            setSite(siteData);
            const rsvpsRes = await fetch(`${API}/sites/${siteData.site_id}/rsvps`, { credentials: 'include' });
            if (rsvpsRes.ok) {
              const rsvpsData = await rsvpsRes.json();
              setRsvps(rsvpsData);
            }
          } else {
            navigate('/onboarding');
          }
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const handleExportCSV = async () => {
    try {
      const res = await fetch(`${API}/sites/${site.site_id}/rsvps/export`, {
        credentials: 'include'
      });

      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rsvps_${site.slug}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("CSV exportado com sucesso!");
      }
    } catch (error) {
      toast.error("Erro ao exportar CSV");
    }
  };

  const confirmedCount = rsvps.filter(r => r.attending).length;
  const totalGuests = rsvps.filter(r => r.attending).reduce((sum, r) => sum + (r.guests || 1), 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-16 h-16 border-4 border-[#F3C9CF] border-t-[#B3121B] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white border-b border-[#F3C9CF]/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              className="text-[#6E0F1A]"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Users className="w-6 h-6 text-[#B3121B]" />
              <span className="font-serif text-xl text-[#6E0F1A]">Confirmações (RSVP)</span>
            </div>
          </div>
          {rsvps.length > 0 && (
            <Button
              onClick={handleExportCSV}
              variant="outline"
              className="border-[#B3121B] text-[#B3121B] rounded-full"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Exportar CSV
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Total de Respostas</p>
                  <p className="text-3xl font-semibold text-[#6E0F1A] mt-1">{rsvps.length}</p>
                </div>
                <div className="w-12 h-12 bg-[#F3C9CF]/50 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-[#B3121B]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Confirmados</p>
                  <p className="text-3xl font-semibold text-[#567D50] mt-1">{confirmedCount}</p>
                </div>
                <div className="w-12 h-12 bg-[#567D50]/10 rounded-full flex items-center justify-center">
                  <Check className="w-6 h-6 text-[#567D50]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-dashboard">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#6E0F1A]/60 uppercase tracking-wide">Total de Convidados</p>
                  <p className="text-3xl font-semibold text-[#6E0F1A] mt-1">{totalGuests}</p>
                </div>
                <div className="w-12 h-12 bg-[#CFAFE6]/30 rounded-full flex items-center justify-center">
                  <Heart className="w-6 h-6 text-[#6E0F1A]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* RSVP List */}
        {rsvps.length === 0 ? (
          <Card className="card-dashboard text-center py-16">
            <CardContent>
              <div className="w-20 h-20 bg-[#F3C9CF]/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-10 h-10 text-[#B3121B]" />
              </div>
              <h2 className="font-serif text-2xl text-[#6E0F1A] mb-3">
                Nenhuma confirmação ainda
              </h2>
              <p className="text-[#6E0F1A]/70 mb-6">
                As confirmações aparecerão aqui quando os convidados responderem
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card className="card-dashboard overflow-hidden">
            <CardHeader>
              <CardTitle className="font-serif text-xl text-[#6E0F1A]">
                Lista de Confirmações
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#FAFAFA]">
                      <TableHead className="text-[#6E0F1A]">Nome</TableHead>
                      <TableHead className="text-[#6E0F1A]">Contato</TableHead>
                      <TableHead className="text-[#6E0F1A]">Acompanhantes</TableHead>
                      <TableHead className="text-[#6E0F1A]">Restrições</TableHead>
                      <TableHead className="text-[#6E0F1A]">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rsvps.map((rsvp) => (
                      <TableRow key={rsvp.rsvp_id} data-testid={`rsvp-row-${rsvp.rsvp_id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium text-[#6E0F1A]">{rsvp.name}</p>
                            {rsvp.message && (
                              <p className="text-xs text-[#6E0F1A]/50 mt-1 line-clamp-1">
                                "{rsvp.message}"
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-1 text-sm text-[#6E0F1A]/70">
                              <Mail className="w-3 h-3" />
                              {rsvp.email}
                            </div>
                            {rsvp.phone && (
                              <div className="flex items-center gap-1 text-sm text-[#6E0F1A]/70">
                                <Phone className="w-3 h-3" />
                                {rsvp.phone}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium text-[#6E0F1A]">{rsvp.guests}</span>
                        </TableCell>
                        <TableCell>
                          {rsvp.dietary_restrictions ? (
                            <div className="flex items-center gap-1 text-sm text-[#6E0F1A]/70">
                              <Utensils className="w-3 h-3" />
                              {rsvp.dietary_restrictions}
                            </div>
                          ) : (
                            <span className="text-[#6E0F1A]/40">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {rsvp.attending ? (
                            <Badge className="bg-[#567D50] hover:bg-[#567D50]">
                              <Check className="w-3 h-3 mr-1" />
                              Confirmado
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-[#F3C9CF] text-[#6E0F1A]">
                              <X className="w-3 h-3 mr-1" />
                              Não irá
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
